// kossay: ملف جديد - نافذة "الحافظة" للأرقام المحفوظة (اختيار / إضافة / حذف)
import {
  useAddSavedNumber,
  useDeleteSavedNumber,
  useSavedNumbers,
} from "api/Client/product";
import { Spinner } from "components";
import CustomInput from "components/ClientLayoutPages/ClientInputField/CustomInput";
import { useState } from "react";
import ClientActionModal from "../ClientActionModal/ClientActionModal";

/**
 * @param {{
 *  isOpen: boolean,
 *  toggle: (open: boolean) => void,
 *  productType: "syriatel" | "mtn",   // نوع المنتج المفتوح
 *  onSelect: (record: object) => void, // يُستدعى عند اختيار سجل مطابق للمنتج
 * }} props
 */
export default function SavedNumbersModal({
  isOpen,
  toggle,
  productType,
  onSelect,
}) {
  const [search, setSearch] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRecord, setNewRecord] = useState({ name: "", number: "", code: "" });

  const { data, isLoading } = useSavedNumbers(isOpen);
  const { mutate: addNumber, isPending: addIsPending } = useAddSavedNumber();
  const { mutate: deleteNumber, isPending: deleteIsPending } =
    useDeleteSavedNumber();

  const records = (data?.data || []).filter((item) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      String(item.name || "").toLowerCase().includes(q) ||
      String(item.number || "").includes(q)
    );
  });

  const handleAdd = (e) => {
    e.preventDefault();
    addNumber(
      {
        name: newRecord.name,
        number: newRecord.number,
        type: productType,
        ...(productType === "mtn" ? { code: newRecord.code } : {}),
      },
      {
        onSuccess: () => {
          setNewRecord({ name: "", number: "", code: "" });
          setShowAddForm(false);
        },
      },
    );
  };

  return (
    <ClientActionModal
      open={isOpen}
      close={toggle}
      size="xxSmall"
      title="الحافظة"
    >
      <div className="grid grid-cols-1 gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="portal-outline-button shrink-0 rounded-xl px-5 py-2.5 font-bold"
            onClick={() => setShowAddForm((prev) => !prev)}
          >
            {showAddForm ? "إلغاء" : "إضافة"}
          </button>
          <input
            type="text"
            className="portal-input h-11 w-full px-4 py-2.5 text-sm"
            placeholder="البحث ..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {showAddForm ? (
          <form
            onSubmit={handleAdd}
            className="portal-panel-soft grid grid-cols-1 gap-3 rounded-[1.35rem] p-4"
          >
            <CustomInput
              id="saved-name"
              title="الاسم"
              value={newRecord.name}
              onChange={(v) => setNewRecord((p) => ({ ...p, name: v.value }))}
              required
            />
            <CustomInput
              id="saved-number"
              title="رقم الهاتف"
              value={newRecord.number}
              onChange={(v) => setNewRecord((p) => ({ ...p, number: v.value }))}
              direction="ltr"
              required
            />
            {productType === "mtn" ? (
              <CustomInput
                id="saved-code"
                title="الكود"
                value={newRecord.code}
                onChange={(v) => setNewRecord((p) => ({ ...p, code: v.value }))}
                direction="ltr"
                required
              />
            ) : null}
            <button
              type="submit"
              className="portal-outline-button mx-auto w-full max-w-xs rounded-xl px-4 py-3"
              disabled={addIsPending}
            >
              {addIsPending ? <Spinner sm /> : "حفظ"}
            </button>
          </form>
        ) : null}

        {isLoading ? (
          <div className="portal-page-card mx-auto flex items-center justify-center rounded-[1.35rem] p-6">
            <Spinner sm />
          </div>
        ) : records.length === 0 ? (
          <div
            className="portal-page-card rounded-[1.35rem] p-6 text-center text-sm font-bold"
            style={{ color: "var(--layout-text-muted)" }}
          >
            لا توجد أرقام محفوظة
          </div>
        ) : (
          <div className="grid max-h-[55vh] grid-cols-1 gap-3 overflow-y-auto">
            {records.map((item) => {
              // السجل الذي لا يطابق نوع المنتج يظهر باهتاً ولا يمكن اختياره
              const matches = item.type === productType;
              return (
                <div
                  key={item.id}
                  role="button"
                  tabIndex={matches ? 0 : -1}
                  onClick={() => matches && onSelect(item)}
                  className={`portal-page-card relative rounded-[1.35rem] p-4 pl-14 ${
                    matches
                      ? "cursor-pointer transition hover:brightness-110"
                      : "cursor-not-allowed opacity-40"
                  }`}
                >
                  <button
                    type="button"
                    disabled={deleteIsPending}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNumber(item.id);
                    }}
                    className="absolute left-3 top-3 flex h-8 w-8 items-center justify-center rounded-lg border border-rose-500/40 bg-rose-500/10 font-bold text-rose-300 transition hover:bg-rose-500/20"
                  >
                    X
                  </button>

                  <h5
                    className="text-lg font-bold"
                    style={{ color: "var(--layout-text)" }}
                  >
                    {item.name}
                  </h5>
                  <p
                    className="mt-1 text-sm"
                    style={{ color: "var(--layout-text-muted)" }}
                  >
                    رقم الهاتف:{" "}
                    <span style={{ direction: "ltr", display: "inline-block" }}>
                      {item.number}
                    </span>
                  </p>
                  {item.code ? (
                    <p
                      className="text-sm"
                      style={{ color: "var(--layout-text-muted)" }}
                    >
                      الكود:{" "}
                      <span
                        style={{ direction: "ltr", display: "inline-block" }}
                      >
                        {item.code}
                      </span>
                    </p>
                  ) : null}
                  <span className="portal-accent mt-2 inline-block rounded-full border border-current px-3 py-0.5 text-xs font-semibold">
                    {item.type}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <button
          type="button"
          className="mx-auto w-full max-w-xs rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-3 font-bold text-rose-300 transition hover:bg-rose-500/20"
          onClick={() => toggle(false)}
        >
          اغلاق
        </button>
      </div>
    </ClientActionModal>
  );
}
